// Constants
import { ADOBE_COMMON_FIELDS } from 'AppConstants';

// Utils
import pubSub from 'UtilityComponents/pubSub';
import { getCookie, getProfileDataProps, saveServerLogs } from 'UtilityComponents/Util';

let analyticsSdk = false;

export const ADOBE_EVENT = 'adobeEvent';
export const analyticsPubSub = pubSub();

let subscriptionList = [];

const SCRIPT_INTERVAL = 1000;
const SCRIPT_CHECK_LIMIT = 8;

const startAdobe = async () => {
	if (typeof window !== 'undefined' && window) {
		const { initAndGetAnalyticsSdk } = await import('@mmt/event-logger');
		try {
			analyticsSdk = initAndGetAnalyticsSdk({ org: 'MMT' });
		} catch (e) {
			saveServerLogs(e.stack, 'error', 'Adobe analytics tracking failed');
		}
	}
};

/**
 * @function publishAnalyticsEvents
 *
 * @author - MMT11933
 * @description - Publishes analytics events to all subscribers and cleans up subscriptions
 * This function is called when Adobe analytics SDK becomes available
 * It notifies all queued event handlers to execute their analytics calls and then unsubscribes them to prevent memory leaks
 */
const publishAnalyticsEvents = () => {
	analyticsPubSub.publish(ADOBE_EVENT, true);
	subscriptionList.forEach((subscription) => subscription.unsubscribe());
};

/**
 * @function canFireAdobe
 *
 * @author - MMT11933
 * @description - Checks if Adobe analytics SDK is available
 * @return {boolean} - True if Adobe analytics SDK is available, false otherwise
 */
const canFireAdobe = () => {
	const mmtAuth = getCookie('mmt-auth');
	return !!(analyticsSdk && (!mmtAuth || (getProfileDataProps('email'))));
};

let initializationInterval = null;

/**
 * @function
 *
 * @author - MMT11933
 * @description - Initializes Adobe analytics SDK
 */
(async function() {
	await startAdobe();
	if (typeof window !== 'undefined' && window) {
		let callCount = 0;
		let isAvailable = canFireAdobe();
		if (!isAvailable) {
			initializationInterval = setInterval(() => {
				isAvailable = canFireAdobe();
				if (isAvailable) {
					publishAnalyticsEvents();
					clearInterval(initializationInterval);
					initializationInterval = null;
					return; // Exit early to prevent further execution
				}
				if (++callCount >= SCRIPT_CHECK_LIMIT) {
					clearInterval(initializationInterval);
					initializationInterval = null;
					return; // Exit early to prevent further execution
				}
			}, SCRIPT_INTERVAL);
		} else {
			publishAnalyticsEvents();
		}
	}
})();

/**
 * @function getCommonPageContext
 *
 * @author - MMT11933
 * @description - Generates common page context data for all analytics events
 * @param {string} pageName - The name of the page
 * @return {object} - Common page context object
 */
const getCommonPageContext = (pageName) => ({
	page_type: ADOBE_COMMON_FIELDS.LANDING,
	funnel_step: ADOBE_COMMON_FIELDS.LANDING,
	lob: ADOBE_COMMON_FIELDS.COMMON,
	lob_category: ADOBE_COMMON_FIELDS.COMMON,
	page_name: pageName,
	org_id: getProfileDataProps('orgId'),
	org_type: getProfileDataProps('orgType'),
	domain: getProfileDataProps('domain'),
	user_type: getProfileDataProps('userType')
});

/**
 * @function pageLoadEvent
 *
 * @author - MMT11933
 * @description - Pushes page load event to Adobe analytics SDK
 */
const pageLoadEvent = ({ pageName, eventValue, extraOptions }) => {
	const eventData = {
		page_context: getCommonPageContext(pageName),
		event_detail: {
			event_type: ADOBE_COMMON_FIELDS.PAGE_EVENT,
			event_name: ADOBE_COMMON_FIELDS.PAGE_EVENT_NAME,
			event_value: eventValue
		}
	};

	analyticsSdk.pushToAdobeDataLayer(eventData, extraOptions);
};

/**
 * @function pushPageLoadToAdobe
 *
 * @author - MMT11933
 * @description - Pushes page load event to Adobe analytics SDK
 * @param {string} pageName - The name of the page
 * @param {string} eventValue - The value of the event
 * @param {object} extraOptions - Extra options for the event
 */
export const pushPageLoadToAdobe = (pageName, eventValue = '', extraOptions = {}) => {
	try {
		if (typeof window === 'undefined') {
			return;
		} else if (!canFireAdobe()) {
			const subscription = analyticsPubSub.subscribe(ADOBE_EVENT, () => {
				pageLoadEvent({ pageName, eventValue, extraOptions });
			});
			subscriptionList.push(subscription);
		} else {
			pageLoadEvent({ pageName, eventValue, extraOptions });
		}
	} catch (e) {
		saveServerLogs(e.stack, 'error', 'Adobe analytics tracking failed');
	}
};

/**
 * @function clickEvent
 *
 * @author - MMT11933
 * @description - Pushes click event to Adobe analytics SDK
 */
const clickEvent = ({ pageName, eventValue, extraOptions }) => {
	const eventPayload = {
		page_context: getCommonPageContext(pageName),
		event_detail: {
			event_type: ADOBE_COMMON_FIELDS.ACTION_EVENT,
			event_name: ADOBE_COMMON_FIELDS.ACTION_EVENT_NAME,
			event_value: eventValue
		}
	};

	analyticsSdk.pushToAdobeDataLayer(eventPayload, extraOptions);
};

/**
 * @function pushClickToAdobe
 *
 * @author - MMT11933
 * @description - Pushes click event to Adobe analytics SDK
 * @param {string} pageName - The name of the page
 * @param {string} eventValue - The value of the event
 * @param {object} extraOptions - Extra options for the event
 */
export const pushClickToAdobe = (pageName, eventValue = '', extraOptions = {}) => {
	try {
		if (typeof window === 'undefined') {
			return;
		} else if (!canFireAdobe()) {
			const subscription = analyticsPubSub.subscribe(ADOBE_EVENT, () => {
				clickEvent({ pageName, eventValue, extraOptions });
			});
			subscriptionList.push(subscription);
		} else {
			clickEvent({ pageName, eventValue, extraOptions });
		}
	} catch (e) {
	}
};
